/**
 * The part of a received letter's inline style that may stay on our page.
 *
 * Received mail used to lose its whole `style` attribute (`mailHtml.ts`). That
 * closed the real dangers - text painted in a colour that only reads on white,
 * an overlay drawn over the CRM - but it also took the harmless layout with it:
 * our own automatic letters, read back in the inbox, lost the bold name, the
 * gaps and the colour bar of their signature, and every other letter lost its
 * bold words and its padding.
 *
 * So a short list of properties is kept, each with the values it may have.
 * Everything that can hide text, move a box or paint an area stays out: no
 * `color`, no `background`, no `position`, `display`, `opacity`, `font-size`,
 * `width` or `height`. A border may have a colour, because a 4px line cannot
 * cover text. A value with a function, a comment or an escape in it is dropped
 * whole, which is how `url()` and `expression()` never get a parser to beat.
 *
 * Pure, so the list can be tested without a DOM.
 */

/**
 * A length we allow: unsigned and never a function, capped per unit so that
 * no unit reaches far past 100px (6em is about 96px; 10% of a 600px column).
 */
const LENGTH_CAP: Record<string, number> = { px: 100, pt: 75, em: 6, rem: 6, "%": 10 };

function isLength(v: string): boolean {
  const m = /^(\d+(?:\.\d+)?)(px|pt|em|rem|%)?$/.exec(v);
  if (!m) return false;
  const n = Number(m[1]);
  const unit = m[2];
  if (unit === undefined) return n === 0;
  return n <= (LENGTH_CAP[unit] ?? 0);
}

function lengths(max: number) {
  return (v: string): boolean => {
    const parts = v.split(/\s+/);
    return parts.length >= 1 && parts.length <= max && parts.every(isLength);
  };
}

/** `3px solid #2563eb`: a thin line, with a hex or a named colour. */
function isThinBorder(v: string): boolean {
  const m = /^(\d+(?:\.\d+)?)px\s+(solid|dashed|dotted)(?:\s+(#[0-9a-f]{3,8}|[a-z]+))?$/.exec(v);
  return !!m && Number(m[1]) <= 4;
}

const oneOf = (...words: string[]) => (v: string): boolean => words.includes(v);

const RULES: Record<string, (value: string) => boolean> = {
  "font-weight": (v) => /^(normal|bold|bolder|lighter|[1-9]00)$/.test(v),
  "font-style": oneOf("normal", "italic", "oblique"),
  "text-decoration": oneOf("none", "underline", "line-through"),
  "text-decoration-line": oneOf("none", "underline", "line-through"),
  "text-align": oneOf("left", "right", "center", "justify", "start", "end"),
  "vertical-align": oneOf("top", "middle", "bottom", "baseline", "text-top", "text-bottom"),
  "white-space": oneOf("normal", "nowrap", "pre", "pre-wrap", "pre-line"),
  "border-collapse": oneOf("collapse", "separate"),
  padding: lengths(4),
  "padding-top": lengths(1),
  "padding-right": lengths(1),
  "padding-bottom": lengths(1),
  "padding-left": lengths(1),
  "margin-top": lengths(1),
  "margin-bottom": lengths(1),
  "border-top": isThinBorder,
  "border-right": isThinBorder,
  "border-bottom": isThinBorder,
  "border-left": isThinBorder,
};

// Looked up through a Map, not the object: `constructor` or `__proto__` as a
// property name must find nothing rather than something inherited.
const ALLOWED = new Map(Object.entries(RULES));

/** Keep only the allowed declarations; an empty answer means "drop the attribute". */
export function safeMailStyle(style: string | null | undefined): string {
  if (!style) return "";
  // A comment, an escape or a function anywhere is not worth parsing around.
  if (/\/\*|\\|\(/.test(style)) return "";
  const kept: string[] = [];
  for (const decl of style.split(";")) {
    const colon = decl.indexOf(":");
    if (colon === -1) continue;
    const prop = decl.slice(0, colon).trim().toLowerCase();
    const value = decl
      .slice(colon + 1)
      .replace(/!\s*important\s*$/i, "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    const ok = ALLOWED.get(prop);
    if (ok && value && ok(value)) kept.push(`${prop}:${value}`);
  }
  return kept.join(";");
}
